import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, Check, Clock, AlertCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";

interface AgentMessage {
  id: string;
  from_agent: string;
  to_agent: string;
  message_type: string;
  content: any;
  status: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
  progress_percentage: number;
  assigned_agent?: string;
  actual_completion?: string;
  completed?: boolean;
  created_at?: string;
  detailed_status?: any;
  estimated_completion?: string;
  priority?: string;
  user_id?: string;
}

interface AuditLog {
  id: string;
  action: string;
  details: {
    agent_id?: string;
    workflow_result?: {
      status: string;
    };
  };
  created_at: string;
}

export const AgentCoordinationPanel = () => {
  const { toast } = useToast();
  const [taskProgress, setTaskProgress] = useState<Record<string, number>>({});
  const queryClient = useQueryClient();

  // Query workflow state first
  const { data: workflowState } = useQuery({
    queryKey: ['workflow-state'],
    queryFn: async () => {
      const { data } = await supabase
        .from('workflow_state')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
  });

  // Query tasks only if workflow is running
  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['agent-tasks', workflowState?.is_running],
    queryFn: async () => {
      if (!workflowState?.is_running) {
        return [];
      }

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching tasks:', error);
        toast({
          variant: "destructive",
          title: "Error fetching tasks",
          description: error.message
        });
        throw error;
      }

      return data;
    },
    enabled: !!workflowState,
  });

  // Query recent activities
  const { data: recentActivities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['recent-activities', workflowState?.is_running],
    queryFn: async () => {
      if (!workflowState?.is_running) {
        return [];
      }

      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('action', 'AGENT_COORDINATION')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) {
        console.error('Error fetching activities:', error);
        toast({
          variant: "destructive",
          title: "Error fetching activities",
          description: error.message
        });
        throw error;
      }
      return data as AuditLog[];
    },
    enabled: !!workflowState,
  });

  // Subscribe to task updates
  useEffect(() => {
    if (!workflowState?.is_running) {
      setTaskProgress({});
      return;
    }

    console.log('Setting up task update subscription...');
    const channel = supabase
      .channel('task-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        (payload: any) => {
          console.log('Received task update:', payload);
          if (payload.new) {
            setTaskProgress(prev => ({
              ...prev,
              [payload.new.id]: payload.new.progress_percentage || 0
            }));
            // Invalidate queries to refresh the UI
            queryClient.invalidateQueries({ queryKey: ['agent-tasks'] });
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up task update subscription...');
      supabase.removeChannel(channel);
    };
  }, [workflowState?.is_running, queryClient]);

  if (tasksLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Coordination Status</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Task Chain Visualization */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Task Dependencies</h3>
            <div className="space-y-4">
              {!workflowState?.is_running || !tasks?.length ? (
                <p className="text-sm text-muted-foreground">No active tasks</p>
              ) : (
                tasks?.map((task) => (
                  <div key={task.id} className="flex items-center gap-2">
                    <div className="flex-1 p-4 border rounded-lg bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{task.title}</span>
                        <Badge variant={
                          task.status === 'completed' ? 'default' :
                          task.status === 'processing' ? 'secondary' :
                          'outline'
                        }>
                          {task.status === 'processing' ? `${taskProgress[task.id] || 0}%` : task.status}
                        </Badge>
                      </div>
                      <Progress 
                        value={taskProgress[task.id] || 0} 
                        className="h-2"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activities */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Recent Activities</h3>
            <div className="space-y-2">
              {activitiesLoading ? (
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded" />
                  ))}
                </div>
              ) : !workflowState?.is_running || !recentActivities?.length ? (
                <p className="text-sm text-muted-foreground">No recent activities</p>
              ) : (
                recentActivities?.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      {activity.details.workflow_result?.status === 'success' ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : activity.details.workflow_result?.status === 'error' ? (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="text-sm">
                        Agent {activity.details.agent_id} - {activity.details.workflow_result?.status || 'pending'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(activity.created_at).toLocaleString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};